import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent } from '@lexical/utils';
import {
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  LexicalEditor,
  LexicalNode,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { MutableRefObject, useEffect, useRef } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { BracketsIcon } from '@/components/icons/BracketsIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import {
  $createFontSizeNode,
  $isFontSizeNode,
} from '@/components/TextEditor/plugins/FontSizePlugin/FontSizeNode';
import { $createPresentationVariableNode } from '@/components/TextEditor/plugins/PresentationVariablePlugin/PresentationVariableNode';
import { zIndexes } from '@/styles/zIndexes';

import { useVariableNames } from './hooks/useVariableNames';
import { useSlideEditorPromptContext } from './SlideEditor.promptContext';
import { useSlideEditorToolbarContext } from './SlideEditor.toolbar';
import { useToolbarDropdown } from './SlideEditor.toolbarDropdown';

const LowPriority = 1;

/**
 * Moves sibling inline nodes into the adjacent font-size run of `fontSize`.
 *
 * Lexical inserts nodes at the edge of a font-size run outside the run, so a
 * variable inserted at the end of 24pt text renders at the base size, and so
 * does text typed after it. The nodes join the run before them, else the run
 * after them, else a new run.
 */
function $moveIntoFontSizeRun(nodes: LexicalNode[], fontSize: number) {
  const first = nodes[0];
  if (!first) return;
  const previous = first.getPreviousSibling();
  if ($isFontSizeNode(previous) && previous.getFontSize() === fontSize) {
    previous.append(...nodes);
    return;
  }
  const next = nodes[nodes.length - 1]?.getNextSibling();
  if ($isFontSizeNode(next) && next.getFontSize() === fontSize) {
    next.splice(0, 0, nodes);
    return;
  }
  const run = $createFontSizeNode(fontSize);
  first.insertBefore(run);
  run.append(...nodes);
}

function insertVariable(
  editor: LexicalEditor,
  variableName: string,
  savedSelectionRef: MutableRefObject<RangeSelection | null>
) {
  editor.update(() => {
    // Restore the saved selection — the popover steals focus from the editor,
    // so $getSelection() would return null without this.
    const savedSelection = savedSelectionRef.current;
    if (
      savedSelection &&
      $getNodeByKey(savedSelection.anchor.key) &&
      $getNodeByKey(savedSelection.focus.key)
    ) {
      $setSelection(savedSelection.clone());
    }

    // Insert the atomic node directly. Inserting marker text first lets the
    // variable transform replace the selection's text node, which leaves the
    // selection pointing to a deleted node and crashes on the next interaction.
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      // No valid selection — move cursor to end of root and insert there
      $getRoot().selectEnd();
      const newSelection = $getSelection();
      if (!$isRangeSelection(newSelection)) return;
      const variableNode = $createPresentationVariableNode(`{${variableName}}`);
      newSelection.insertNodes([variableNode]);
      variableNode.selectNext(0, 0);
    } else {
      // Auto-space before variable if the preceding character isn't whitespace.
      // Only check when the start is a TextNode — on ElementNodes, offset is
      // a child index, not a character position, so the lookup would be wrong.
      // insertNodes replaces a ranged selection and inserts at its start, so
      // read the start point, which is the focus of a backward selection.
      const start = selection.isBackward() ? selection.focus : selection.anchor;
      const startNode = start.getNode();
      let needsSpace = false;
      if ($isTextNode(startNode)) {
        const charBefore = startNode.getTextContent()[start.offset - 1];
        needsSpace =
          start.offset > 0 && charBefore !== ' ' && charBefore !== '\u00a0';
      }
      const fontSizeNode = $findMatchingParent(startNode, $isFontSizeNode);
      const variableNode = $createPresentationVariableNode(`{${variableName}}`);
      const insertedNodes = [
        ...(needsSpace ? [$createTextNode(' ')] : []),
        variableNode,
      ];
      selection.insertNodes(insertedNodes);
      if (fontSizeNode) {
        $moveIntoFontSizeRun(insertedNodes, fontSizeNode.getFontSize());
      }
      variableNode.selectNext(0, 0);
    }
  });
  // Defer focus until after the MUI Menu fully closes — the menu's onClick
  // triggers a React state update that unmounts the Modal/focus-trap, which
  // restores focus to the anchor button. A synchronous editor.focus() would
  // run before that cleanup, so the button would steal focus back.
  requestAnimationFrame(() => {
    editor.focus();
  });
}

/**
 * Variable insertion button for the slide editor toolbar. Opens a dropdown
 * with available variable names.
 *
 * Hidden when editing a prompt on a template (variables are presentation-time
 * values that don't belong in prompts).
 *
 * Must be rendered inside both a SlideEditorToolbar context and a
 * LexicalComposer context.
 */
export function SlideEditorVariableButton() {
  const promptContext = useSlideEditorPromptContext();
  const variableNames = useVariableNames();
  const [editor] = useLexicalComposerContext();
  const { activeDropdown, setActiveDropdown } = useSlideEditorToolbarContext();
  const variableDropdown = useToolbarDropdown('variable');

  // Saved selection so we can restore cursor position after the variable
  // popover steals focus from the Lexical editor.
  const savedSelectionRef = useRef<RangeSelection | null>(null);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          savedSelectionRef.current = selection.clone();
        }
        return false;
      },
      LowPriority
    );
  }, [editor]);

  // Hide when editing a prompt on a template
  if (promptContext?.isTemplate && promptContext.customPrompt) return null;

  return (
    <div ref={variableDropdown.containerRef}>
      <ButtonWithPopover
        label="Add variable"
        size="xs"
        variant="transparent"
        popperVariant="menuBelow"
        startIcon={BracketsIcon}
        highlightWhenOpen
        isOpen={activeDropdown === 'variable'}
        onClick={() =>
          setActiveDropdown((prev) => (prev === 'variable' ? null : 'variable'))
        }
        onClose={() =>
          setActiveDropdown((prev) => (prev === 'variable' ? null : prev))
        }
        popperProps={{
          // Prevent MUI Menu from returning focus to the trigger button
          // after closing — we restore focus to the Lexical editor instead.
          disableRestoreFocus: true,
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
          transformOrigin: { horizontal: 'left', vertical: 'top' },
          // 9px = 5px (toolbar padding-bottom 4px + border 1px) + 4px desired gap,
          // matching the block dropdown's visual alignment below the toolbar border.
          PaperProps: { sx: { mt: '9px' } },
          sx: { zIndex: zIndexes.TOOLTIP },
        }}
      >
        {variableNames.map((variableName) => (
          <MenuItem
            key={variableName}
            label={variableName}
            onClick={() => {
              insertVariable(editor, variableName, savedSelectionRef);
              setActiveDropdown(null);
            }}
          />
        ))}
      </ButtonWithPopover>
    </div>
  );
}
